import React, { FC, useEffect, useMemo, useState } from 'react';

import { DataGroupSensitivityCategory } from '@ariksa/abac/api';
import { Box, Grid, HStack, Stack } from '@chakra-ui/react';
import { FiGrid } from 'react-icons/fi';
import { useSelector } from 'react-redux';
import { colorOpacity, customTheme } from 'theme';

import { BorderedBox } from 'components/DataDisplay/Box/BorderedBox';
import { FilterLabelWrapper } from 'components/DataDisplay/Box/BorderedBox/FilterLabelWrapper';
import { ButtonGroup } from 'components/DataEntry';
import { SearchBox } from 'components/DataEntry/Input/SearchBox';
import { ClockIcon } from 'components/Icons/SvgResourcesIcon';
import {
  BlockStorageIcon,
  ButtonGroupItem,
  DatabaseIcon,
  DataGroupIcon,
  getIconColor,
  HistoricalTimelineIcon,
  IconTypes,
  LabelIcon,
  NotificationIcon,
  NotifyIcon,
  PageHeaderWithIcon,
  RefreshIcon,
} from 'components/index';
import { SummaryCard } from 'containers/Dashboard/utils/SummaryCard';

import { selectDataCatalog } from '../selectors';

import { DetailCard } from './DetailCard';
import { viewDataCatalogByOptions } from './utils';

interface Props {
  showCards?: boolean;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  viewBy?: ButtonGroupItem;
  onViewByChange?: (viewBy) => void;
}

export const CatalogSummary: FC<Props> = props => {
  const { dataGroups, dataGroupSensitivity, dataGroupStatuses } = useSelector(
    selectDataCatalog,
  );
  const { searchTerm, onSearchChange, viewBy, onViewByChange } = props;
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);

  const summary = dataGroups.data?.summary ?? {};
  const catalogDataGroups = dataGroups.data?.data_groups;
  const sensitivityLabelCount = useMemo(() => {
    const sensitivityLabels = new Set<string>();

    (catalogDataGroups ?? []).forEach(dataGroup => {
      dataGroupSensitivity?.[
        dataGroup.uuid
      ]?.data?.sensitivity_categories?.forEach(
        label =>
          label !== DataGroupSensitivityCategory.Unknown &&
          sensitivityLabels.add(label),
      );
    });

    return sensitivityLabels.size;
  }, [catalogDataGroups, dataGroupSensitivity]);
  const sensitivityLoading = (catalogDataGroups ?? []).some(
    dataGroup => dataGroupSensitivity?.[dataGroup.uuid]?.isLoading,
  );
  const jitActiveCount =
    dataGroupStatuses.data?.data_groups?.reduce(
      (total, dataGroup) => total + (dataGroup.active_requests?.length ?? 0),
      0,
    ) ?? 0;

  useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  const detailCards = [
    // {
    //   label: 'Data Sources',
    //   count: 0,
    //   icon: <BlockStorageIcon color={customTheme.colors.green['300']} />,
    //   footer: {
    //     content: <Box>0 in last 30 days</Box>,
    //   },
    //   info: {
    //     message: (
    //       <Box color={customTheme.colors.primary}>0 sensitive records</Box>
    //     ),
    //     bg: colorOpacity(customTheme.colors.primary, 0.1),
    //   },
    // },
    {
      label: 'Data Groups',
      count: dataGroups?.data?.data_groups?.length,
      icon: <DataGroupIcon />,
      footer: {
        content: (
          <Box>
            {dataGroups.data?.summary?.data_groups_created_last_30_days_count ??
              0}{' '}
            in last 30 days
          </Box>
        ),
      },
      isLoading: dataGroups?.isLoading,
      // info: {
      //   message: <Box color={customTheme.colors.primary}>0 tables</Box>,
      //   bg: colorOpacity(customTheme.colors.primary, 0.1),
      // },
    },
    {
      label: 'Access Type',
      icon: <HistoricalTimelineIcon />,
      count: summary.permanent_data_group_count ?? 0,
      footer: {
        content: (
          <Box>
            {(summary.permanent_data_group_count ?? 0) +
              ' permanent, ' +
              (summary?.permanent_data_group_count ?? 0) +
              ' JIT based'}
          </Box>
        ),
      },
      info: {
        message: (
          <Box color={customTheme.colors.primary}>
            {!!jitActiveCount ? jitActiveCount + ' active' : 'None active'}
          </Box>
        ),
        bg: colorOpacity(customTheme.colors.primary, 0.1),
      },
    },
    {
      label: 'Sensitive Records',
      icon: <LabelIcon />,
      count: sensitivityLabelCount ?? 0,
      isLoading: sensitivityLoading,
      footer: {
        content: <Box>sensitivity labels</Box>,
      },
    },
    {
      label: 'JIT Requests',
      icon: <NotifyIcon />,
      count: dataGroupStatuses?.data?.total_requests_count ?? 0,
      footer: {
        content: (
          <Box>
            {dataGroupStatuses?.data?.requests_last_30_days_count ?? 0} in the
            last 30 days
          </Box>
        ),
      },
      // info: {
      //   message: <Box color={customTheme.colors.primary}>0 hours</Box>,
      //   bg: colorOpacity(customTheme.colors.primary, 0.1),
      // },
    },
  ];

  return (
    <Stack spacing={4} w="full">
      <Box>
        <PageHeaderWithIcon
          icon={<FiGrid size="100%" />}
          label="Data Catalog"
        />
      </Box>
      <HStack spacing={2}>
        <FilterLabelWrapper>VIEW BY</FilterLabelWrapper>
        <ButtonGroup
          items={viewDataCatalogByOptions}
          value={viewBy}
          onClick={onViewByChange}
        />
      </HStack>
      {props.showCards && (
        <HStack spacing={4} w="full">
          {detailCards.map(card => (
            <BorderedBox key={card.label} w="full" py={0} maxW="330px">
              <SummaryCard {...card} />
            </BorderedBox>
          ))}
        </HStack>
      )}
      {/*<Box w="96">
        <SearchBox
          value={localSearchTerm}
          onChange={setLocalSearchTerm}
          onSearch={onSearchChange}
        />
      </Box>*/}
    </Stack>
  );
};
